import { formatMultiplierXMLParser } from '@virtus/common/utils/format-multiplier-xml-parser/formatMultiplierXMLParser';
import { GlideMappedDataTypes } from 'src/components/forms/form-elements';
import { FormEntity } from 'src/components/forms/glide-data-content/glide-data-content.model';
import { ActionArguments, Argument } from 'src/models/order/glideActionArguments.api.model';
import SearchService from 'src/services/search.service';

export interface GlideFormElementTypeParams {
  dataType: string;
  fieldName: string;
  hasLookUps: boolean;
}

export const getFormElementType = ({
  dataType,
  fieldName,
  hasLookUps,
}: GlideFormElementTypeParams): GlideMappedDataTypes => {
  switch (dataType) {
    case 'bit':
      return 'checkbox';
    case 'decimal':
      return 'number';
    case 'datetime':
      return 'calendar';
    case 'largestring':
    case 'string':
      return 'text';
    case 'object': {
      if (fieldName.toLowerCase() === 'position') return 'toggler';
      if (!hasLookUps) return 'search';
      return 'select';
    }
    case 'objectcollection':
      return 'tableModal';
    default:
      console.warn(`Unhandled data type dataType ${dataType}. Defaulting to decimal`);
      return 'number';
  }
};

const getOptionColor = (itemName: string) => {
  if (itemName.toLowerCase() === 'sell') return 'var(--red)';
  if (itemName.toLowerCase() === 'buy') return 'var(--green)';
  return undefined;
};

export const getOptions = (lookups?: { [key: string]: string }) => {
  if (!lookups) return {};
  const nextLookups: any = {};
  Object.entries(lookups).forEach(([key, value]: any) => {
    nextLookups[key] = {
      name: value,
      value: key,
      color: getOptionColor(value),
    };
  });
  return nextLookups;
};

const getBooleanValue = (value: string | undefined) => Boolean(value === 'true');

const getDefaultValue = (dataType: string, elementType: string, defaultValue?: any) => {
  if (dataType === 'bit') return getBooleanValue(defaultValue as string);
  if (dataType === 'object' && elementType === 'select' && defaultValue) {
    return Object.values(defaultValue)[0];
  }
  return defaultValue || '';
};

export const compareOrder = (key1: string, key2: string, elements: { [key: string]: Argument }) => {
  if (elements[key1].ordering < elements[key2].ordering) {
    return -1;
  }
  if (elements[key1].ordering > elements[key2].ordering) {
    return 1;
  }

  return 0;
};

export const mapActionArgumentsToActionForm = (actionArguments: ActionArguments) => {
  if (!actionArguments) {
    return {
      name: '',
      formFields: {},
      actionUri: '',
    };
  }
  const elements = actionArguments?.arguments;
  const sortedArgumentKeys = Object.keys(elements).sort((a, b) => compareOrder(a, b, elements));

  const filterArguments = sortedArgumentKeys.filter(
    key => key !== 'instance/argument/add_to_hypo_scenario_target_scenario',
  );

  const mappedActionEntity: FormEntity = {
    name: actionArguments?.display_name,
    actionUri: actionArguments?.action_uri,
    formFields: filterArguments.reduce((accumulatedFields, argument_uri) => {
      const argument: Argument = actionArguments?.arguments[argument_uri];
      const formElementType = getFormElementType({
        dataType: argument.argument_type.toLowerCase(),
        fieldName: argument.display_name,
        hasLookUps: argument.hasOwnProperty('lookups'),
      });
      const dataType = argument.argument_type.toLowerCase();
      const object_type = argument.object_type_?.toLowerCase();
      const searchType = object_type ? object_type.replace('object_types/', '') : '';
      return {
        ...accumulatedFields,
        [argument_uri]: {
          name: argument.argument_name,
          displayName: argument.display_name,
          required: argument.argument_required,
          dataType,
          searchType,
          searchService: SearchService().search,
          formElementType,
          readonly: false,
          defaultValue: getDefaultValue(dataType, formElementType, argument?.argument_default as string),
          options: getOptions(argument?.lookups),
          formatMultiplier: argument.format_multiplier
            ? formatMultiplierXMLParser(argument.format_multiplier)
            : undefined,
        },
      };
    }, {}),
  };

  return mappedActionEntity;
};
